import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { generations, label, lifespan, orbit, type Distant, type Neighbor } from '../../../shared/tree'
import { type Tree, type TreePerson } from '@/lib/tree'
import { connectors, type Box, type Connector, type Placed } from '@/lib/treeLines'
import { cn } from '@/lib/utils'
import { Avatar } from './Avatar'

// One person's neighborhood: them in the middle, parents above, partners beside, brothers and sisters alongside,
// children and pets below and family friends nearby, with grandparents over the parents and grandchildren under the
// children. A pet's people sit above it, like parents. Tapping someone walks over to them: their face glides to the middle,
// everyone else's face moves to where they now sit, newcomers settle in and the lines draw out from the middle.
// Tapping the middle opens their page. Only one person's people at a time, never the whole chart.
export function Orbit({
  familyId,
  tree,
  focus,
  children,
}: {
  familyId: string
  tree: Tree
  focus: TreePerson
  children?: ReactNode
}) {
  const people = new Map(tree.people.map((p) => [p.id, p]))
  const around = orbit(focus.id, tree.links)
  const above = [...around.parents, ...around.petParents]
  const below = [...around.children, ...around.pets]
  const further = generations(focus.id, tree.links, around)
  const box = useRef<HTMLDivElement>(null)
  const block = useRef<HTMLDivElement>(null)
  const svg = useRef<SVGSVGElement>(null)
  // The lines, and which of them should draw themselves out when they first show.
  const [lines, setLines] = useState<{ list: Connector[]; draw: { all: boolean; known: Set<string> } | null }>({
    list: [],
    draw: null,
  })
  const [lit, setLit] = useState<string | null>(null)
  // Where every face was the last time things were laid out, and who was in the middle then.
  const was = useRef<{ focus: string | null; faces: Map<string, Box>; lines: Set<string> }>({
    focus: null,
    faces: new Map(),
    lines: new Set(),
  })

  useLayoutEffect(() => {
    const root = box.current
    if (!root) return
    const placed = (id: string): Placed | null => {
      const tile = root.querySelector<HTMLElement>(`[data-person="${CSS.escape(id)}"]`)
      const face = tile?.querySelector<HTMLElement>('[data-face]')
      return tile && face ? { id, tile: boxIn(tile, root), face: boxIn(face, root) } : null
    }
    const group = (list: Neighbor[]) => list.flatMap((n) => placed(n.id) ?? [])
    const through = (list: Distant[]) =>
      list.flatMap((n) => {
        const p = placed(n.id)
        return p ? [{ ...p, via: n.via }] : []
      })
    // Offsets, not getBoundingClientRect, so a face mid-glide is measured where it's headed.
    const measure = () => {
      const middle = placed(focus.id)
      if (!middle || !block.current) return null
      const layout = {
        face: middle.face,
        block: boxIn(block.current, root),
        parents: group(above),
        siblings: group(around.siblings),
        partners: group(around.partners),
        children: group(below),
        grandparents: through(further.grandparents),
        grandchildren: through(further.grandchildren),
      }
      const faces = new Map(
        [
          middle,
          ...layout.parents,
          ...layout.siblings,
          ...layout.partners,
          ...layout.children,
          ...layout.grandparents,
          ...layout.grandchildren,
          ...group(around.friends),
        ].map((p) => [p.id, p.face]),
      )
      return { faces, list: connectors(layout) }
    }

    const measured = measure()
    if (!measured) return
    const { faces } = measured
    const before = was.current
    const walked = before.focus !== null && before.focus !== focus.id
    if (!reducedMotion()) {
      let order = 0
      for (const [id, face] of faces) {
        const tile = root.querySelector<HTMLElement>(`[data-person="${CSS.escape(id)}"]`)
        const el = tile?.querySelector<HTMLElement>('[data-face]')
        const words = tile?.querySelector<HTMLElement>('[data-words]')
        const from = before.faces.get(id)
        if (from && el) {
          // Someone already on screen glides from their old place, growing or shrinking into the new one.
          if (from.x === face.x && from.y === face.y && from.w === face.w) continue
          const dx = from.x + from.w / 2 - (face.x + face.w / 2)
          const dy = from.y + from.h / 2 - (face.y + face.h / 2)
          el.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${from.w / face.w})` }, { transform: 'none' }], {
            duration: 560,
            easing: SETTLE,
          })
          words?.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 320,
            delay: 240,
            easing: 'ease-out',
            fill: 'backwards',
          })
        } else if (tile) {
          // Someone new settles in, one after another, nearest first.
          tile.animate(
            [
              { opacity: 0, transform: 'scale(0.86)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 420, delay: (walked ? 180 : 0) + order++ * 45, easing: SETTLE, fill: 'backwards' },
          )
        }
      }
    }
    const draw = reducedMotion() ? null : { all: walked || before.focus === null, known: before.lines }
    setLines({ list: measured.list, draw })
    was.current = { focus: focus.id, faces, lines: new Set(measured.list.map((l) => l.id)) }

    // The lines follow whatever the screen's width does, without being drawn again.
    const observer = new ResizeObserver(() => {
      const next = measure()
      if (!next) return
      was.current.faces = next.faces
      setLines({ list: next.list, draw: null })
    })
    observer.observe(root)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- around is derived from focus and tree
  }, [focus.id, tree])

  // New lines draw themselves out from the middle once the faces are on their way.
  useLayoutEffect(() => {
    const pending = lines.draw
    if (!pending || !svg.current) return
    for (const path of svg.current.querySelectorAll<SVGPathElement>('path[data-id]')) {
      if (!pending.all && pending.known.has(path.dataset.id!)) continue
      path.animate(
        [
          { strokeDashoffset: 1, opacity: 0 },
          { strokeDashoffset: 1, opacity: 1, offset: 0.01 },
          { strokeDashoffset: 0, opacity: 1 },
        ],
        { duration: 620, delay: pending.all ? 220 : 120, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'backwards' },
      )
    }
  }, [lines])

  const hover = (id: string) => ({
    onPointerEnter: () => setLit(id),
    onPointerLeave: () => setLit(null),
    onFocus: () => setLit(id),
    onBlur: () => setLit(null),
  })

  const node = (n: Neighbor | Distant) => {
    const p = people.get(n.id)
    if (!p) return null
    const word = label(n.relation, p.referAs)
    return (
      <li key={n.id}>
        <Link
          to={`/circle/${familyId}/people/${p.id}`}
          data-person={p.id}
          {...hover(p.id)}
          aria-label={`${p.name}, ${focus.name}’s ${word}`}
          className="group relative flex w-[4.75rem] flex-col items-center gap-1.5 rounded-2xl p-1 text-center outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:w-24"
        >
          <span data-face className="relative block rounded-full">
            <Avatar
              person={p}
              className="size-12 text-lg transition-[scale,box-shadow] duration-300 ease-settle group-hover:scale-[1.06] group-hover:shadow-lift sm:size-14"
            />
          </span>
          <span data-words className="flex flex-col items-center gap-0.5">
            <span className="line-clamp-2 text-sm leading-tight font-medium">{p.name}</span>
            <span className="text-xs text-muted-foreground">{word}</span>
          </span>
        </Link>
      </li>
    )
  }

  const row = (title: string, group: (Neighbor | Distant)[], className?: string) =>
    group.length > 0 && (
      <ul aria-label={title} className={cn('flex flex-wrap justify-center gap-x-2 gap-y-5 sm:gap-x-4', className)}>
        {group.map(node)}
      </ul>
    )

  const years = lifespan(focus)
  return (
    <div className="tree-canvas relative -mx-2 overflow-hidden rounded-[2rem] border border-border/70 px-1 py-8 sm:mx-0 sm:px-6 sm:py-10">
      <div ref={box} className="relative flex flex-col items-center gap-10">
        <svg
          ref={svg}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 size-full overflow-visible"
          fill="none"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {lines.list.map((l) => (
            <path key={l.id} data-id={l.id} d={l.d} pathLength={1} strokeDasharray="1 1" className="tree-line" />
          ))}
          {lines.list
            .filter((l) => l.id === lit)
            .map((l) => (
              <path key="lit" d={l.d} className="tree-line-lit" />
            ))}
        </svg>

        {row('Grandparents', further.grandparents)}

        {row(around.parents.length ? 'Parents' : 'Pet parents', above)}

        <div className="grid w-full grid-cols-[1fr_auto_1fr] items-start gap-1 sm:gap-6">
          {row('Brothers and sisters', around.siblings, 'flex-col items-end justify-self-end pt-7 sm:pt-8') || <span />}
          <div ref={block} className="relative flex flex-col items-center gap-3">
            <Link
              data-person={focus.id}
              to={`/person/${focus.id}`}
              aria-label={`Open ${focus.name}’s page`}
              className="group relative flex w-32 flex-col items-center gap-2 rounded-3xl p-2 text-center outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:w-44"
            >
              <span data-face className="relative block rounded-full">
                <Avatar
                  person={focus}
                  variant="display"
                  className="size-24 text-4xl transition-[scale,box-shadow] duration-300 ease-settle group-hover:scale-[1.03] group-hover:shadow-lift sm:size-28"
                />
              </span>
              <span data-words className="flex flex-col items-center gap-1">
                {!focus.living && <span className="eyebrow text-muted-foreground">In memory</span>}
                <span className="text-xl leading-tight font-semibold tracking-tight text-balance">{focus.name}</span>
                {years && <span className="text-sub text-muted-foreground">{years}</span>}
              </span>
            </Link>
            {children}
          </div>
          {row('Partners', around.partners, 'flex-col items-start justify-self-start pt-7 sm:pt-8') || <span />}
        </div>

        {row(!around.pets.length ? 'Children' : around.children.length ? 'Children and pets' : 'Pets', below)}

        {row('Grandchildren', further.grandchildren)}

        {around.friends.length > 0 && (
          <div className="flex flex-col items-center gap-3 border-t border-dashed border-border pt-6 sm:px-10">
            <p className="eyebrow text-muted-foreground">Family friends</p>
            {row('Family friends', around.friends)}
          </div>
        )}
      </div>
    </div>
  )
}

const SETTLE = 'cubic-bezier(0.25, 0.9, 0.3, 1.15)'
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

// Where an element sits inside root, from layout offsets, which ignore any transform it's in the middle of.
function boxIn(el: HTMLElement, root: HTMLElement): Box {
  let x = 0
  let y = 0
  let at: HTMLElement | null = el
  while (at && at !== root) {
    x += at.offsetLeft
    y += at.offsetTop
    at = at.offsetParent as HTMLElement | null
  }
  return { x, y, w: el.offsetWidth, h: el.offsetHeight }
}
